import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  MessageCircle,
  Plus,
  Search,
  Heart,
  MessageSquare,
  Eye,
  Pin,
  Lock,
  Trash2,
  Edit2,
  X,
  Send,
  ChevronLeft,
  User,
  Clock,
  Filter,
  TrendingUp,
  Loader2,
  Dumbbell,
  Apple,
  Trophy,
  HelpCircle,
  UserPlus,
  Utensils
} from 'lucide-react';

const CATEGORY_CONFIG = {
  general: { name: 'General Discussion', icon: MessageCircle, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  workouts: { name: 'Workouts & Training', icon: Dumbbell, color: 'text-red-400', bg: 'bg-red-500/10' },
  nutrition: { name: 'Nutrition & Diet', icon: Apple, color: 'text-green-400', bg: 'bg-green-500/10' },
  motivation: { name: 'Motivation & Support', icon: Heart, color: 'text-pink-400', bg: 'bg-pink-500/10' },
  progress: { name: 'Progress & Wins', icon: Trophy, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  questions: { name: 'Q&A', icon: HelpCircle, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  introductions: { name: 'Introductions', icon: UserPlus, color: 'text-teal-400', bg: 'bg-teal-500/10' },
  recipes: { name: 'Recipes & Meal Ideas', icon: Utensils, color: 'text-orange-400', bg: 'bg-orange-500/10' }
};

export default function ForumPage() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newThread, setNewThread] = useState({ title: '', content: '', category: 'general' });
  const [replyContent, setReplyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const isTrainer = user?.role === 'trainer' || user?.role === 'admin';

  const fetchThreads = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (categoryFilter) params.append('category', categoryFilter);
      if (searchTerm) params.append('search', searchTerm);
      params.append('sort', sortBy);
      
      const res = await axios.get(`${API}/forum/threads?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setThreads(res.data.threads || []);
    } catch (error) {
      console.error('Error fetching threads:', error);
    } finally {
      setLoading(false);
    }
  }, [token, categoryFilter, searchTerm, sortBy]);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  const fetchThread = async (threadId) => {
    try {
      const res = await axios.get(`${API}/forum/threads/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedThread(res.data);
    } catch (error) {
      toast.error('Failed to load thread');
    }
  };

  const handleCreateThread = async () => {
    if (!newThread.title.trim() || !newThread.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/forum/threads`, newThread, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Thread created');
      setShowCreateModal(false);
      setNewThread({ title: '', content: '', category: 'general' });
      fetchThreads();
    } catch (error) {
      toast.error('Failed to create thread');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim()) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/forum/threads/${selectedThread.id}/replies`, 
        { content: replyContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reply posted');
      setReplyContent('');
      fetchThread(selectedThread.id);
    } catch (error) {
      toast.error('Failed to post reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLikeThread = async (threadId) => {
    try {
      const res = await axios.post(`${API}/forum/threads/${threadId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (selectedThread?.id === threadId) {
        setSelectedThread(prev => ({
          ...prev,
          user_liked: res.data.liked,
          like_count: prev.like_count + (res.data.liked ? 1 : -1)
        }));
      }
      
      setThreads(prev => prev.map(t => 
        t.id === threadId 
          ? { ...t, user_liked: res.data.liked, like_count: t.like_count + (res.data.liked ? 1 : -1) }
          : t
      ));
    } catch (error) {
      toast.error('Failed to like');
    }
  };

  const handleLikeReply = async (replyId) => {
    try {
      const res = await axios.post(`${API}/forum/replies/${replyId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedThread(prev => ({
        ...prev,
        replies: prev.replies.map(r => 
          r.id === replyId 
            ? { ...r, user_liked: res.data.liked, like_count: r.like_count + (res.data.liked ? 1 : -1) }
            : r
        )
      }));
    } catch (error) {
      toast.error('Failed to like');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  const ThreadCard = ({ thread }) => {
    const category = CATEGORY_CONFIG[thread.category] || CATEGORY_CONFIG.general;
    const CategoryIcon = category.icon;
    
    return (
      <div 
        className="bg-card border border-border rounded-lg p-4 hover:border-primary/50 transition-all cursor-pointer"
        onClick={() => fetchThread(thread.id)}
      >
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${category.bg}`}>
            <CategoryIcon className={`w-5 h-5 ${category.color}`} />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {thread.is_pinned && <Pin className="w-4 h-4 text-yellow-400" />}
              {thread.is_locked && <Lock className="w-4 h-4 text-gray-500" />}
              <h3 className="text-white font-semibold truncate">{thread.title}</h3>
            </div>
            
            <p className="text-gray-400 text-sm line-clamp-2 mb-2">{thread.content}</p>
            
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <div className="flex items-center gap-1">
                <User className="w-3.5 h-3.5" />
                <span>{thread.author?.name || 'Anonymous'}</span>
                {thread.author?.is_trainer && (
                  <span className="px-1.5 py-0.5 bg-primary/20 text-primary rounded text-xs">Trainer</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                <span>{formatDate(thread.created_at)}</span>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col items-end gap-2 text-sm">
            <div className="flex items-center gap-3 text-gray-500">
              <span className="flex items-center gap-1">
                <MessageSquare className="w-4 h-4" />
                {thread.reply_count}
              </span>
              <span className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                {thread.view_count}
              </span>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); handleLikeThread(thread.id); }}
              className={`flex items-center gap-1 ${thread.user_liked ? 'text-red-400' : 'text-gray-500 hover:text-red-400'}`}
            >
              <Heart className={`w-4 h-4 ${thread.user_liked ? 'fill-current' : ''}`} />
              {thread.like_count}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  // Thread detail view
  if (selectedThread) {
    const category = CATEGORY_CONFIG[selectedThread.category] || CATEGORY_CONFIG.general;
    const CategoryIcon = category.icon;
    
    return (
      <div className="space-y-6" data-testid="forum-thread-view">
        <button
          onClick={() => setSelectedThread(null)}
          className="flex items-center gap-2 text-gray-400 hover:text-white"
        >
          <ChevronLeft className="w-5 h-5" />
          Back to Forum
        </button>
        
        {/* Thread */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className={`p-3 rounded-lg ${category.bg}`}>
              <CategoryIcon className={`w-6 h-6 ${category.color}`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                {selectedThread.is_pinned && <Pin className="w-4 h-4 text-yellow-400" />}
                <h1 className="text-xl font-bold text-white">{selectedThread.title}</h1>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-400">
                <span className={`px-2 py-0.5 rounded ${category.bg} ${category.color}`}>
                  {category.name}
                </span>
                <span>{formatDate(selectedThread.created_at)}</span>
                <span>{selectedThread.view_count} views</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-start gap-4">
            {selectedThread.author?.image ? (
              <img src={selectedThread.author.image} alt="" className="w-12 h-12 rounded-full" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                <User className="w-6 h-6 text-primary" />
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-white font-medium">{selectedThread.author?.name}</span>
                {selectedThread.author?.is_trainer && (
                  <span className="px-2 py-0.5 bg-primary/20 text-primary rounded text-xs">Trainer</span>
                )}
              </div>
              <p className="text-gray-300 whitespace-pre-wrap">{selectedThread.content}</p>
              
              <div className="flex items-center gap-4 mt-4">
                <button
                  onClick={() => handleLikeThread(selectedThread.id)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                    selectedThread.user_liked 
                      ? 'bg-red-500/20 text-red-400' 
                      : 'bg-secondary text-gray-400 hover:text-red-400'
                  }`}
                >
                  <Heart className={`w-4 h-4 ${selectedThread.user_liked ? 'fill-current' : ''}`} />
                  {selectedThread.like_count} Likes
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Replies */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">
            {selectedThread.replies?.length || 0} Replies
          </h2>
          
          {selectedThread.replies?.map(reply => (
            <div key={reply.id} className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-start gap-3">
                {reply.author?.image ? (
                  <img src={reply.author.image} alt="" className="w-10 h-10 rounded-full" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                    <User className="w-5 h-5 text-gray-400" />
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white font-medium">{reply.author?.name}</span>
                    {reply.author?.is_trainer && (
                      <span className="px-1.5 py-0.5 bg-primary/20 text-primary rounded text-xs">Trainer</span>
                    )}
                    <span className="text-gray-500 text-sm">{formatDate(reply.created_at)}</span>
                    {reply.edited && <span className="text-gray-500 text-xs">(edited)</span>}
                  </div>
                  <p className="text-gray-300">{reply.content}</p>
                  
                  <button
                    onClick={() => handleLikeReply(reply.id)}
                    className={`flex items-center gap-1 mt-2 text-sm ${
                      reply.user_liked ? 'text-red-400' : 'text-gray-500 hover:text-red-400'
                    }`}
                  >
                    <Heart className={`w-4 h-4 ${reply.user_liked ? 'fill-current' : ''}`} />
                    {reply.like_count}
                  </button>
                </div>
              </div>
            </div>
          ))}
          
          {/* Reply form */}
          {!selectedThread.is_locked && (
            <div className="bg-card border border-border rounded-lg p-4">
              <textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white resize-none mb-3"
                placeholder="Write a reply..."
              />
              <div className="flex justify-end">
                <Button onClick={handleReply} disabled={submitting || !replyContent.trim()}>
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Post Reply
                </Button>
              </div>
            </div>
          )}
          
          {selectedThread.is_locked && (
            <div className="bg-secondary/50 border border-border rounded-lg p-4 text-center text-gray-400">
              <Lock className="w-5 h-5 mx-auto mb-2" />
              This thread is locked
            </div>
          )}
        </div>
      </div>
    );
  }

  // Thread list view
  return (
    <div className="space-y-6" data-testid="forum-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Community Forum</h1>
          <p className="text-gray-400">Connect, share, and support each other</p>
        </div>
        
        <Button onClick={() => setShowCreateModal(true)} data-testid="new-thread-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Thread
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search threads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary"
          />
        </div>
        
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-2 bg-card border border-border rounded-lg text-white"
        >
          <option value="">All Categories</option>
          {Object.entries(CATEGORY_CONFIG).map(([key, { name }]) => (
            <option key={key} value={key}>{name}</option>
          ))}
        </select>
        
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-4 py-2 bg-card border border-border rounded-lg text-white"
        >
          <option value="recent">Most Recent</option>
          <option value="popular">Most Replies</option>
          <option value="likes">Most Liked</option>
        </select>
      </div>

      {/* Category Pills */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setCategoryFilter('')}
          className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
            !categoryFilter ? 'bg-primary text-white' : 'bg-secondary text-gray-400 hover:text-white'
          }`}
        >
          All
        </button>
        {Object.entries(CATEGORY_CONFIG).map(([key, { name, color, bg }]) => (
          <button
            key={key}
            onClick={() => setCategoryFilter(categoryFilter === key ? '' : key)}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              categoryFilter === key ? `${bg} ${color}` : 'bg-secondary text-gray-400 hover:text-white'
            }`}
          >
            {name}
          </button>
        ))}
      </div>

      {/* Threads */}
      <div className="space-y-3">
        {threads.map(thread => (
          <ThreadCard key={thread.id} thread={thread} />
        ))}
        
        {threads.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No threads yet. Start the conversation!</p>
          </div>
        )}
      </div>

      {/* Create Thread Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-2xl">
            <div className="border-b border-border p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">New Thread</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Category</label>
                <select
                  value={newThread.category}
                  onChange={(e) => setNewThread(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
                >
                  {Object.entries(CATEGORY_CONFIG).map(([key, { name }]) => (
                    <option key={key} value={key}>{name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Title *</label>
                <input
                  type="text"
                  value={newThread.title}
                  onChange={(e) => setNewThread(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
                  placeholder="What's on your mind?"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Content *</label>
                <textarea
                  value={newThread.content}
                  onChange={(e) => setNewThread(prev => ({ ...prev, content: e.target.value }))}
                  rows={6}
                  className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white resize-none"
                  placeholder="Share your thoughts, questions, or experiences..."
                />
              </div>
            </div>
            
            <div className="border-t border-border p-4 flex gap-3">
              <Button variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleCreateThread} disabled={submitting} className="flex-1">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Post Thread
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
